import React, { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { auditAPI, userAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  ClipboardList, 
  Loader2, 
  RefreshCw,
  Download,
  Shield,
  Edit3,
  Plus,
  Trash2,
  LogIn,
  CheckCircle,
  RotateCcw,
  Pause,
  Play,
  ChevronLeft,
  ChevronRight,
  FileText,
  CalendarIcon,
} from 'lucide-react';

// Helper to format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'blank value';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value);
};

// Get icon for action type
const getActionIcon = (action) => {
  if (action?.includes('create') || action?.includes('add')) return Plus;
  if (action?.includes('delete')) return Trash2;
  if (action?.includes('login')) return LogIn;
  if (action?.includes('paid') || action?.includes('finalize')) return CheckCircle;
  if (action?.includes('reverse')) return RotateCcw;
  if (action?.includes('pause')) return Pause;
  if (action?.includes('resume')) return Play;
  return Edit3;
};

// Get color for action type
const getActionColor = (action) => {
  if (action?.includes('create') || action?.includes('add')) return 'text-emerald-600';
  if (action?.includes('delete')) return 'text-red-600';
  if (action?.includes('login')) return 'text-gray-600';
  if (action?.includes('paid')) return 'text-emerald-600';
  if (action?.includes('finalize')) return 'text-indigo-600';
  if (action?.includes('reverse')) return 'text-orange-600';
  if (action?.includes('pause')) return 'text-amber-600';
  if (action?.includes('resume')) return 'text-green-600';
  return 'text-blue-600';
};

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export default function AuditLogsPage() {
  const { isAdminLevel } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    hasMore: false
  });
  const [filters, setFilters] = useState({
    user_email: 'all',
    date_filter: 'all',
    date_from: null,
    date_to: null,
  });

  // Redirect non-super admins
  useEffect(() => {
    if (!isAdminLevel()) {
      toast.error('Access denied. Super Admin only.');
      navigate('/dashboard');
    }
  }, [isAdminLevel, navigate]);

  useEffect(() => {
    if (isAdminLevel()) {
      loadData();
    }
  }, []);

  useEffect(() => {
    if (!loading && isAdminLevel()) {
      loadLogs();
    }
  }, [filters, pagination.page, itemsPerPage]);

  const loadData = async () => {
    try {
      try {
        const usersRes = await userAPI.getAll();
        setUsers(usersRes.data);
      } catch (e) {
        console.log('Could not load users for filter');
      }
      await loadLogs();
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const params = {
        limit: itemsPerPage,
        skip: (pagination.page - 1) * itemsPerPage
      };
      if (filters.user_email !== 'all') params.user_email = filters.user_email;
      if (filters.date_filter !== 'all' && filters.date_filter !== 'custom') {
        params.date_filter = filters.date_filter;
      }
      if (filters.date_filter === 'custom') {
        if (filters.date_from) {
          params.date_from = format(filters.date_from, 'yyyy-MM-dd');
        }
        if (filters.date_to) {
          params.date_to = format(filters.date_to, 'yyyy-MM-dd');
        }
      }
      
      const response = await auditAPI.getAll(params);
      const data = response.data;
      
      // Handle both old format (array) and new format (object with logs)
      if (Array.isArray(data)) {
        setLogs(data);
        setPagination(prev => ({ ...prev, total: data.length, hasMore: false }));
      } else {
        setLogs(data.logs || []);
        setPagination(prev => ({
          ...prev,
          total: data.total || 0,
          hasMore: data.has_more || false
        }));
      }
    } catch (error) {
      console.error('Failed to load logs:', error);
    }
  };

  // Format date for group header
  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Format time
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format full date time for "by" line
  const formatByDateTime = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get human-readable changes from log
  const getChangesDescription = (log) => {
    const changes = [];
    const action = log.action || '';
    const before = log.before_value || {};
    const after = log.after_value || {};
    const changesSummary = log.changes_summary || [];
    
    // If we have a description from the backend, use it
    if (log.description) {
      changes.push(log.description);
    }
    
    // Add changes summary if available
    if (changesSummary.length > 0) {
      changesSummary.forEach(change => {
        if (!changes.includes(change)) {
          changes.push(change);
        }
      });
    }
    
    // If no description, generate from action
    if (changes.length === 0) {
      const entityName = log.record_name || getEntityName(log);
      const actorName = getActorName(log);
      
      switch (action) {
        case 'user.login':
          changes.push(`${actorName} logged in`);
          break;
        case 'user.create':
          changes.push(`New user "${entityName}" was created`);
          if (after.role) changes.push(`Role set to ${after.role.replace(/_/g, ' ')}`);
          break;
        case 'user.update':
          changes.push(`User "${entityName}" was updated`);
          break;
        case 'user.disable':
          changes.push(`User "${entityName}" was disabled`);
          break;
        case 'employee.create':
          changes.push(`New employee "${entityName}" was added`);
          if (after.gross_salary) changes.push(`Gross Salary set to ${formatCurrency(after.gross_salary)}`);
          break;
        case 'employee.update':
          changes.push(`Employee "${entityName}" was updated`);
          generateFieldChanges(before, after, changes);
          break;
        case 'employee.delete':
          changes.push(`Employee "${before.name || entityName}" was deleted`);
          break;
        case 'salary.lop_update':
          changes.push(`LOP Days updated for "${entityName}"`);
          if (before.lop_days !== undefined && after.lop_days !== undefined) {
            changes.push(`LOP Days changed from ${before.lop_days || 0} to ${after.lop_days || 0}`);
          }
          break;
        case 'salary.reward_update':
          changes.push(`Reward updated for "${entityName}"`);
          break;
        case 'salary.finalize':
          changes.push(`Salary finalized for "${entityName}"`);
          if (after.net_payable) changes.push(`Net Payable: ${formatCurrency(after.net_payable)}`);
          break;
        case 'salary.mark_paid':
          changes.push(`Salary marked as paid for "${entityName}"`);
          if (after.net_payable) changes.push(`Amount: ${formatCurrency(after.net_payable)}`);
          break;
        case 'salary.reverse':
          changes.push(`Payment reversed for "${entityName}"`);
          break;
        case 'advance.create':
          changes.push(`New advance "${entityName}" was created`);
          if (after.total_amount || after.amount) {
            changes.push(`Amount: ${formatCurrency(after.total_amount || after.amount)}`);
          }
          break;
        case 'advance.update':
          changes.push(`Advance "${entityName}" was updated`);
          break;
        case 'advance.delete':
          changes.push(`Advance "${entityName}" was deleted`);
          break;
        case 'advance.pause':
          changes.push(`Advance "${entityName}" was paused`);
          break;
        case 'advance.resume':
          changes.push(`Advance "${entityName}" was resumed`);
          break;
        case 'org.create':
          changes.push(`New organization "${entityName}" was created`);
          break;
        case 'org.update':
          changes.push(`Organization "${entityName}" was updated`);
          break;
        case 'org.delete':
          changes.push(`Organization "${entityName}" was deleted`);
          break;
        case 'settings.update':
          changes.push(`System settings were updated`);
          break;
        default:
          changes.push(`${action.replace(/\./g, ' ').replace(/_/g, ' ')} on "${entityName}"`);
      }
    }
    
    return changes;
  };

  // Generate field-level changes
  const generateFieldChanges = (before, after, changes) => {
    const fieldsToCheck = ['name', 'gross_salary', 'status', 'lop_days', 'reward_amount'];
    
    fieldsToCheck.forEach(field => {
      if (before[field] !== after[field] && (before[field] || after[field])) {
        const beforeVal = before[field] || 'blank value';
        const afterVal = after[field] || 'blank value';
        const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        
        if (field.includes('salary') || field.includes('amount')) {
          changes.push(`${fieldName} was updated from ${formatCurrency(beforeVal)} to ${formatCurrency(afterVal)}`);
        } else {
          changes.push(`${fieldName} was updated from "${beforeVal}" to "${afterVal}"`);
        }
      }
    });
  };

  // Get entity name fallback
  const getEntityName = (log) => {
    if (log.record_name && log.record_name !== 'Unknown' && log.record_name !== 'Record') return log.record_name;
    if (log.after_value?.name) return log.after_value.name;
    if (log.after_value?.employee_name) return log.after_value.employee_name;
    if (log.after_value?.email) return log.after_value.email;
    if (log.before_value?.name) return log.before_value.name;
    if (log.before_value?.employee_name) return log.before_value.employee_name;
    // For salary records, try to build name from employee info
    if (log.entity_type === 'salary_record') {
      const empName = log.after_value?.employee_name || log.before_value?.employee_name;
      const month = log.after_value?.month || log.before_value?.month;
      const year = log.after_value?.year || log.before_value?.year;
      if (empName && month && year) {
        const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June', 
                          'July', 'August', 'September', 'October', 'November', 'December'];
        return `${empName} – ${monthNames[month]} ${year}`;
      }
    }
    return 'Record';
  };

  // Get actor name (user's actual name)
  const getActorName = (log) => {
    if (log.actor_name && log.actor_name !== 'Unknown') return log.actor_name;
    if (log.user_email) {
      // Try to find user name from users list
      const user = users.find(u => u.email === log.user_email);
      if (user?.name) return user.name;
      return log.user_email.split('@')[0];
    }
    return 'System';
  };

  // Get actor role (formatted role name)
  const getActorRole = (log) => {
    if (log.actor_role && log.actor_role !== 'Unknown') return log.actor_role;
    if (log.user_role) return log.user_role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return 'User';
  };

  // Replace role name with user name in description
  const formatDescription = (description, actorName, actorRole) => {
    if (!description) return description;
    // Replace role name at the start of description with user name
    // Common roles: Super Admin, Lop Manager, Payroll Auditor, Account Executive
    const roles = ['Super Admin', 'Lop Manager', 'Payroll Auditor', 'Account Executive', 'Admin'];
    let formatted = description;
    for (const role of roles) {
      if (formatted.startsWith(role + ' ')) {
        formatted = actorName + ' ' + formatted.substring(role.length + 1);
        break;
      }
    }
    return formatted;
  };

  // Group logs by date
  const groupedLogs = useMemo(() => {
    const groups = {};
    logs.forEach(log => {
      const date = new Date(log.timestamp).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(log);
    });
    return groups;
  }, [logs]);

  // Calculate total pages
  const totalPages = Math.ceil(pagination.total / itemsPerPage);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  // Handle filter change - reset to page 1
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle items per page change
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // CSV Export
  const handleExportCSV = () => {
    const headers = ['Timestamp', 'User', 'Action', 'Details'];
    const rows = logs.map(log => {
      const changes = getChangesDescription(log);
      return [
        new Date(log.timestamp).toISOString(),
        getActorName(log),
        log.action,
        changes.join(' | '),
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Audit logs exported to CSV');
  };

  // PDF Export
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const pdfContent = generatePDFContent();
      const printWindow = window.open('', '_blank');
      printWindow.document.write(pdfContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
      toast.success('PDF export ready - use Print dialog to save as PDF');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  // Generate PDF content as HTML
  const generatePDFContent = () => {
    const dateStr = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    let logsHTML = '';
    
    Object.entries(groupedLogs).forEach(([date, dateLogs]) => {
      logsHTML += `
        <div class="date-group">
          <h3 class="date-header">${formatDateHeader(date)}</h3>
          <div class="logs">
      `;
      
      dateLogs.forEach(log => {
        const changes = getChangesDescription(log);
        const actorName = getActorName(log);
        const actorRole = getActorRole(log);
        
        // Format changes to show user name instead of role
        const formattedChanges = changes.map(change => 
          formatDescription(change, actorName, actorRole)
        );
        
        logsHTML += `
          <div class="log-entry">
            <div class="log-time">${formatTime(log.timestamp)}</div>
            <div class="log-content">
              <div class="log-changes">
                ${formattedChanges.map((change, i) => 
                  `<p class="${i === 0 ? 'primary' : 'secondary'}">${change}</p>`
                ).join('')}
              </div>
              <p class="log-by">by ${actorRole} • ${formatByDateTime(log.timestamp)}</p>
            </div>
          </div>
        `;
      });
      
      logsHTML += '</div></div>';
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Activity History - PayTrack</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
            padding: 40px;
            color: #333;
            line-height: 1.6;
          }
          .header { 
            border-bottom: 2px solid #e5e7eb; 
            padding-bottom: 20px; 
            margin-bottom: 30px;
          }
          .header h1 { 
            font-size: 24px; 
            font-weight: 600;
            color: #111;
          }
          .header p { 
            color: #666; 
            font-size: 14px;
            margin-top: 5px;
          }
          .date-group { 
            margin-bottom: 30px; 
          }
          .date-header { 
            font-size: 14px; 
            font-weight: 600;
            color: #666;
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 8px;
            margin-bottom: 15px;
          }
          .log-entry { 
            display: flex;
            gap: 20px;
            padding: 12px 0;
            border-bottom: 1px solid #f3f4f6;
          }
          .log-time { 
            width: 80px;
            flex-shrink: 0;
            font-size: 13px;
            color: #666;
          }
          .log-content {
            flex: 1;
          }
          .log-changes p.primary {
            font-weight: 500;
            color: #111;
            font-size: 14px;
          }
          .log-changes p.secondary {
            color: #666;
            font-size: 13px;
            margin-top: 2px;
          }
          .log-by {
            font-size: 12px;
            color: #999;
            margin-top: 8px;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
            font-size: 12px;
            color: #999;
            text-align: center;
          }
          @media print {
            body { padding: 20px; }
            .log-entry { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Activity History</h1>
          <p>PayTrack Audit Log Report • Generated on ${dateStr}</p>
          <p>Total Records: ${pagination.total} • Showing Page ${pagination.page} of ${totalPages}</p>
        </div>
        ${logsHTML}
        <div class="footer">
          <p>PayTrack - Salary Reporting System</p>
        </div>
      </body>
      </html>
    `;
  };

  if (!isAdminLevel()) {
    return null;
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 spinner text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="audit-logs-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Activity History</h1>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Shield className="h-3 w-3" />
                Super Admin only • {pagination.total.toLocaleString()} total records
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadLogs}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCSV}>
              <Download className="mr-2 h-4 w-4" />
              CSV
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={exporting}>
              {exporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileText className="mr-2 h-4 w-4" />
              )}
              PDF
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-end">
          {/* User Filter */}
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Filter by User</Label>
            <Select 
              value={filters.user_email} 
              onValueChange={(value) => handleFilterChange('user_email', value)}
            >
              <SelectTrigger className="w-[180px]" data-testid="user-filter">
                <SelectValue placeholder="All Users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.email}>{u.name || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Date Range Filter */}
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Date Range</Label>
            <Select 
              value={filters.date_filter} 
              onValueChange={(value) => handleFilterChange('date_filter', value)}
            >
              <SelectTrigger className="w-[150px]" data-testid="date-filter">
                <SelectValue placeholder="All Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">Last 30 Days</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Custom Date Range - From */}
          {filters.date_filter === 'custom' && (
            <>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">From Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[140px] justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filters.date_from ? format(filters.date_from, 'MMM d, yyyy') : 'Select'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={filters.date_from}
                      onSelect={(date) => handleFilterChange('date_from', date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">To Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[140px] justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filters.date_to ? format(filters.date_to, 'MMM d, yyyy') : 'Select'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={filters.date_to}
                      onSelect={(date) => handleFilterChange('date_to', date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </>
          )}

          {/* Records Per Page */}
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Records per page</Label>
            <Select 
              value={itemsPerPage.toString()} 
              onValueChange={handleItemsPerPageChange}
            >
              <SelectTrigger className="w-[100px]" data-testid="items-per-page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ITEMS_PER_PAGE_OPTIONS.map(num => (
                  <SelectItem key={num} value={num.toString()}>{num}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Page Info */}
          <div className="text-sm text-muted-foreground">
            Page {pagination.page} of {totalPages || 1}
          </div>
        </div>

        {/* Timeline */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-medium">Timeline History</CardTitle>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No activity logs found</p>
              </div>
            ) : (
              <ScrollArea className="h-[500px] pr-4">
                <div className="space-y-6">
                  {Object.entries(groupedLogs).map(([date, dateLogs]) => (
                    <div key={date} className="space-y-4">
                      {/* Date Header */}
                      <div className="sticky top-0 bg-background z-10 py-2">
                        <h3 className="text-sm font-semibold text-muted-foreground border-b pb-2">
                          {formatDateHeader(date)}
                        </h3>
                      </div>
                      
                      {/* Logs for this date */}
                      <div className="space-y-4">
                        {dateLogs.map((log, index) => {
                          const Icon = getActionIcon(log.action);
                          const iconColor = getActionColor(log.action);
                          const changes = getChangesDescription(log);
                          const actorName = getActorName(log);
                          const actorRole = getActorRole(log);
                          
                          // Format changes to show user name instead of role
                          const formattedChanges = changes.map(change => 
                            formatDescription(change, actorName, actorRole)
                          );
                          
                          return (
                            <div 
                              key={log.id || index}
                              className="flex gap-4 group"
                              data-testid={`activity-log-${index}`}
                            >
                              {/* Time */}
                              <div className="w-20 flex-shrink-0 text-sm text-muted-foreground pt-0.5">
                                {formatTime(log.timestamp)}
                              </div>
                              
                              {/* Icon */}
                              <div className={`flex-shrink-0 mt-0.5 ${iconColor}`}>
                                <Icon className="h-4 w-4" />
                              </div>
                              
                              {/* Content */}
                              <div className="flex-1 min-w-0 space-y-1">
                                {/* Changes */}
                                <div className="space-y-1">
                                  {formattedChanges.map((change, i) => (
                                    <p 
                                      key={i} 
                                      className={`text-sm ${i === 0 ? 'font-medium text-foreground' : 'text-muted-foreground'}`}
                                    >
                                      {change}
                                    </p>
                                  ))}
                                </div>
                                
                                {/* By line - now shows role instead of user name */}
                                <p className="text-xs text-muted-foreground/70 pt-1">
                                  by <span className="font-medium">{actorRole}</span> • {formatByDateTime(log.timestamp)}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            
            <div className="flex items-center gap-2">
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={pagination.page === pageNum ? "default" : "outline"}
                    size="sm"
                    className="w-10"
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Summary */}
        <div className="text-sm text-muted-foreground text-center">
          Showing {((pagination.page - 1) * itemsPerPage) + 1} - {Math.min(pagination.page * itemsPerPage, pagination.total)} of {pagination.total.toLocaleString()} activities
        </div>
      </div>
    </DashboardLayout>
  );
}
